import ListItem from '@tiptap/extension-list-item';
import { Mark } from 'prosemirror-model';
import cloneDeep from 'lodash.clonedeep';
import { TEXT_TAG_TO_STYLES_MAP } from '../TextBasedWidgets.config';
import { marksToListAttr } from '../TextBasedWidgets.helpers';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    listItem: {
      splitListItemKeepMarks: () => ReturnType;
    };
  }
}

interface ListItemOptions {
  HTMLAttributes: Record<string, any>;
  marks: Mark[];
}

export const CustomListItem = ListItem.extend<ListItemOptions>({
  addOptions() {
    return {
      HTMLAttributes: {},
      marks: [],
    };
  },

  addCommands() {
    return {
      /*
       * Press enter -> create a new li item
       * Store the marks from the current selection
       *
       */
      splitListItemKeepMarks:
        () =>
        ({ chain }) => {
          return chain()
            .splitListItem(this.name)
            .command(({ tr, editor }) => {
              const marks =
                this.editor.view.state.storedMarks ||
                (this.editor.view.state.selection.$to.parentOffset && this.editor.view.state.selection.$from.marks());

              if (marks) tr.ensureMarks(marks);

              // should add the styling from the marks to the new list item
              const $from = editor.view.state.selection.$from;
              let { nodeBefore, depth } = $from;

              while (depth > 0 && nodeBefore?.type.name !== 'listItem') {
                nodeBefore = $from.node(depth--);
              }

              const isListItem = nodeBefore?.type.name === 'listItem';
              if (isListItem && marks) {
                const attributes = marksToListAttr(marks);
                this.editor.storage.listItem = cloneDeep(attributes);
              }

              return true;
            })
            .run();
        },
    };
  },

  addAttributes() {
    return {
      fontSize: {
        default: null,
      },
      fontFamily: {
        default: null,
      },
      color: {
        default: null,
      },
      marginLeft: {
        default: null,
      },
      bold: {
        default: null,
      },
      italic: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [{ tag: 'li' }];
  },

  renderHTML({ node }) {
    let markAttr = node.attrs;
    let childAttr = node.attrs;

    // if the li node has no marks, derive the marks from the child text node attributes
    const hasNoAttributes =
      markAttr.fontSize === null ||
      markAttr.fontFamily === null ||
      markAttr.color === null ||
      markAttr.bold === null ||
      markAttr.italic === null;

    if (hasNoAttributes) {
      // find child marks
      let child = node.firstChild;
      while (!!child && !child.isText) {
        child = child.firstChild;
      }

      let marks: readonly Mark[] = [];
      if (child) marks = child.marks;

      // When the text is completely deleted and a new list is created from typing `*, - or 1.`
      // Get the marks from the options (stored by the EmptyTextStyle Extension)
      if (!marks.length && this.options.marks) {
        marks = this.options.marks;
        // reset the options marks
        this.options.marks = [];
      }

      childAttr = marksToListAttr(marks);

      const isEmpty = node.textContent === '';
      if (isEmpty && node.firstChild && node.firstChild.type.name === 'paragraph') {
        childAttr = { ...childAttr, fontSize: node.firstChild.attrs.fontSize };
      }
    }

    // apply bold / italic to the ::marker of the li
    // else it will be applied for all the text in the child p nodes
    const boldPseudoMarkerClass = childAttr.bold || markAttr.bold ? 'li-marker-bold' : '';
    const italicPseudoMarkerClass = childAttr.italic || markAttr.italic ? 'li-marker-italic' : '';

    const fontSize = childAttr.fontSize || markAttr.fontSize || `${TEXT_TAG_TO_STYLES_MAP.paragraph.fontSize}px`;
    const fontFamily = childAttr.fontFamily || markAttr.fontFamily || TEXT_TAG_TO_STYLES_MAP.paragraph.fontFamily;
    const color = childAttr.color || markAttr.color || TEXT_TAG_TO_STYLES_MAP.paragraph.color;

    return [
      'li',
      {
        class: `${boldPseudoMarkerClass} ${italicPseudoMarkerClass}`,
        style: `font-size: ${fontSize}; color: ${color}; font-family: ${fontFamily}; margin-left: ${fontSize}`,
      },
      0,
    ];
  },

  addKeyboardShortcuts() {
    return {
      Enter: () => this.editor.commands.splitListItemKeepMarks(),
      Tab: () => this.editor.commands.sinkListItem(this.name),
      'Shift-Tab': () => this.editor.commands.liftListItem(this.name),
    };
  },
});
